import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  PhoneIcon,
  ArrowUturnLeftIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  EyeSlashIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import Notification from "../components/Notification";
import PlanGatedPage from "../components/PlanGatedPage";
import { useNavigate } from "react-router-dom";

interface TwilioMessage {
  sid: string;
  from: string;
  to: string;
  body: string;
  status: string;
  direction: string;
  dateCreated: string;
  dateSent: string;
  errorCode?: string;
  errorMessage?: string;
  price?: string;
  priceUnit?: string;
}

interface SMSConfig {
  isConfigured: boolean;
  provider: string;
  fromNumber?: string;
}

interface SMSContact {
  id: string;
  studio_id: string;
  phone_number: string;
  display_name: string;
  linked_student_id?: string | null;
  linked_lead_id?: string | null;
  linked_staff_id?: string | null;
}

interface KnownContact {
  name: string;
  type: "student" | "lead" | "staff";
}

interface SMSHiddenConversation {
  id: string;
  studio_id: string;
  phone_number: string;
  created_at: string;
}

interface Conversation {
  phone: string;
  lastMessage: TwilioMessage;
  messageCount: number;
}

function formatPhoneForSmsE164(phone: string): string {
  let cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) {
    if (cleaned.length === 10) {
      cleaned = "+1" + cleaned;
    } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
      cleaned = "+" + cleaned;
    }
  }
  return cleaned;
}

function phoneDigitsKey(phone: string): string {
  return phone.replace(/\D/g, "");
}

/** Normalize to 10-digit US national number when possible for reliable matching */
function phoneMatchKey(phone: string): string {
  const digits = phoneDigitsKey(phone);
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

function formatPhoneDisplay(phone: string): string {
  const d = phone.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    const n = d.slice(1, 11);
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6, 10)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  }
  return phone;
}

function phonesMatch(a: string, b: string): boolean {
  return phoneMatchKey(a) === phoneMatchKey(b);
}

function getCounterpartyPhone(msg: TwilioMessage, studioNumber: string): string {
  if (phonesMatch(msg.from, studioNumber)) {
    return formatPhoneForSmsE164(msg.to);
  }
  return formatPhoneForSmsE164(msg.from);
}

function buildConversations(
  messages: TwilioMessage[],
  studioNumber: string
): Conversation[] {
  const map = new Map<string, { lastMessage: TwilioMessage; count: number }>();

  for (const msg of messages) {
    const phone = getCounterpartyPhone(msg, studioNumber);
    const existing = map.get(phone);
    if (!existing) {
      map.set(phone, { lastMessage: msg, count: 1 });
      continue;
    }
    existing.count += 1;
    if (
      new Date(msg.dateCreated).getTime() >
      new Date(existing.lastMessage.dateCreated).getTime()
    ) {
      existing.lastMessage = msg;
    }
  }

  return Array.from(map.entries())
    .map(([phone, { lastMessage, count }]) => ({
      phone,
      lastMessage,
      messageCount: count,
    }))
    .sort(
      (a, b) =>
        new Date(b.lastMessage.dateCreated).getTime() -
        new Date(a.lastMessage.dateCreated).getTime()
    );
}

export default function SMSHistory() {
  const { studioUser } = useStudioUser();
  const { getAuthToken } = useAuth();
  const navigate = useNavigate();

  const [messageHistory, setMessageHistory] = useState<TwilioMessage[]>([]);
  const [threadMessages, setThreadMessages] = useState<TwilioMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [loadingMoreThread, setLoadingMoreThread] = useState(false);
  const [conversationSearch, setConversationSearch] = useState("");
  const [messagesLimit, setMessagesLimit] = useState(50);
  const [threadLimit, setThreadLimit] = useState(50);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [hasMoreThread, setHasMoreThread] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [savedContacts, setSavedContacts] = useState<SMSContact[]>([]);
  const [knownContacts, setKnownContacts] = useState<Map<string, KnownContact>>(
    new Map()
  );
  const [smsConfig, setSmsConfig] = useState<SMSConfig>({
    isConfigured: false,
    provider: "twilio",
  });
  const [smsConfigLoading, setSmsConfigLoading] = useState(true);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [deletingName, setDeletingName] = useState(false);
  const [hiddenConversations, setHiddenConversations] = useState<
    SMSHiddenConversation[]
  >([]);
  const [showHiddenSection, setShowHiddenSection] = useState(false);
  const [showHideConfirmModal, setShowHideConfirmModal] = useState(false);
  const [showDeleteNameConfirmModal, setShowDeleteNameConfirmModal] = useState(false);
  const [hidingConversation, setHidingConversation] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    title: "",
  });

  const threadContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollThreadRef = useRef(true);
  const pendingScrollRestoreRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
  } | null>(null);

  const studioNumber = smsConfig.fromNumber || "";

  const isMessageSent = useCallback(
    (msg: TwilioMessage) =>
      msg.direction?.startsWith("outbound") ||
      (!!studioNumber && phonesMatch(msg.from, studioNumber)),
    [studioNumber]
  );

  const getDisplayName = useCallback(
    (phone: string): string => {
      const normalized = formatPhoneForSmsE164(phone);
      const saved = savedContacts.find((c) =>
        phonesMatch(c.phone_number, normalized)
      );
      if (saved?.display_name) return saved.display_name;

      const digits = phoneMatchKey(normalized);
      const known = knownContacts.get(digits);
      if (known?.name) return known.name;

      return formatPhoneDisplay(normalized);
    },
    [savedContacts, knownContacts]
  );

  const conversations = useMemo(() => {
    if (!studioNumber) return [];
    return buildConversations(messageHistory, studioNumber);
  }, [messageHistory, studioNumber]);

  const isPhoneHidden = useCallback(
    (phone: string) =>
      hiddenConversations.some((h) => phonesMatch(h.phone_number, phone)),
    [hiddenConversations]
  );

  const visibleConversations = useMemo(
    () => conversations.filter((conv) => !isPhoneHidden(conv.phone)),
    [conversations, isPhoneHidden]
  );

  const filteredConversations = useMemo(() => {
    const base = visibleConversations;
    if (!conversationSearch.trim()) return base;
    const q = conversationSearch.toLowerCase();
    return base.filter((conv) => {
      const name = getDisplayName(conv.phone).toLowerCase();
      const phone = conv.phone.toLowerCase();
      const preview = conv.lastMessage.body.toLowerCase();
      return name.includes(q) || phone.includes(q) || preview.includes(q);
    });
  }, [visibleConversations, conversationSearch, getDisplayName]);

  useEffect(() => {
    const init = async () => {
      await loadSMSConfig();
    };
    init();
  }, [studioUser?.studio_id]);

  useEffect(() => {
    if (!smsConfigLoading) {
      if (smsConfig.isConfigured) {
        loadMessageHistory(true);
        loadSavedContacts();
        loadKnownContacts();
        loadHiddenConversations();
      } else {
        setHasAttemptedLoad(true);
      }
    }
  }, [smsConfig.isConfigured, smsConfigLoading]);

  useEffect(() => {
    if (selectedPhone && smsConfig.isConfigured) {
      loadThreadMessages(selectedPhone, true);
    } else {
      setThreadMessages([]);
    }
  }, [selectedPhone, smsConfig.isConfigured]);

  const loadSMSConfig = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) {
      setSmsConfigLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-config?studio_id=${studioUser.studio_id}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.ok) {
        const config = await response.json();
        const isConfigured =
          (config.provider === "studio_managed" && !!config.fromnumber) ||
          (config.provider === "own_twilio" && !!config.accountsid) ||
          (!config.provider && !!config.accountsid);

        setSmsConfig({
          isConfigured,
          provider:
            config.provider === "studio_managed" ? "studio_managed" : "twilio",
          fromNumber: config.fromnumber,
        });
      } else {
        setSmsConfig({ isConfigured: false, provider: "twilio" });
      }
    } catch (error) {
      console.error("Error loading SMS config:", error);
      setSmsConfig({ isConfigured: false, provider: "twilio" });
    } finally {
      setSmsConfigLoading(false);
    }
  };

  const loadHiddenConversations = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/hidden-conversations?studio_id=${studioUser.studio_id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setHiddenConversations(data.hidden || []);
      }
    } catch (error) {
      console.error("Error loading hidden conversations:", error);
    }
  };

  const loadSavedContacts = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/contacts?studio_id=${studioUser.studio_id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSavedContacts(data.contacts || []);
      }
    } catch (error) {
      console.error("Error loading SMS contacts:", error);
    }
  };

  const loadKnownContacts = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    try {
      const [studentsResponse, leadsResponse, staffResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioUser.studio_id}?limit=10000`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${studioUser.studio_id}?limit=10000`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/${studioUser.studio_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
      ]);

      if (!studentsResponse.ok || !leadsResponse.ok || !staffResponse.ok) {
        return;
      }

      const [studentsData, leadsData, staffData] = await Promise.all([
        studentsResponse.json(),
        leadsResponse.json(),
        staffResponse.json(),
      ]);

      const students = Array.isArray(studentsData.data) ? studentsData.data : [];
      const leads = Array.isArray(leadsData.data) ? leadsData.data : [];
      const staff = Array.isArray(staffData) ? staffData : [];

      const map = new Map<string, KnownContact>();

      const addContact = (
        phone: string | null | undefined,
        name: string,
        type: KnownContact["type"]
      ) => {
        if (!phone || !name) return;
        const key = phoneMatchKey(phone);
        if (!key || map.has(key)) return;
        map.set(key, { name, type });
      };

      for (const s of students) addContact(s.phone, s.name, "student");
      for (const l of leads) addContact(l.phone, l.name, "lead");
      for (const m of staff) addContact(m.phone, m.name, "staff");

      setKnownContacts(map);
    } catch (error) {
      console.error("Error loading known contacts:", error);
    }
  };

  const loadMessageHistory = async (reset: boolean = false) => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) {
      setHasAttemptedLoad(true);
      return;
    }

    if (reset) {
      setLoadingMessages(true);
      setMessagesLimit(50);
      setMessageHistory([]);
    } else {
      setLoadingMoreMessages(true);
    }

    const currentLimit = reset ? 50 : messagesLimit + 50;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/messages?studio_id=${studioUser.studio_id}&limit=${currentLimit}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const messages = data.messages || [];
        setMessageHistory(messages);
        setMessagesLimit(currentLimit);
        setHasMoreMessages(messages.length >= currentLimit);
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          title: "Error",
          message: errorData.error || "Failed to load message history",
        });
      }
    } catch (error) {
      console.error("Error loading message history:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to load message history",
      });
    } finally {
      setLoadingMessages(false);
      setLoadingMoreMessages(false);
      setHasAttemptedLoad(true);
    }
  };

  const loadThreadMessages = async (phone: string, reset: boolean = false) => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    if (reset) {
      shouldAutoScrollThreadRef.current = true;
      setLoadingThread(true);
      setThreadLimit(50);
      setThreadMessages([]);
    } else {
      shouldAutoScrollThreadRef.current = false;
      const container = threadContainerRef.current;
      pendingScrollRestoreRef.current = container
        ? { scrollTop: container.scrollTop, scrollHeight: container.scrollHeight }
        : null;
      setLoadingMoreThread(true);
    }

    const currentLimit = reset ? 50 : threadLimit + 50;
    const encodedPhone = encodeURIComponent(formatPhoneForSmsE164(phone));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/messages?studio_id=${studioUser.studio_id}&phone=${encodedPhone}&limit=${currentLimit}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const messages = data.messages || [];
        setThreadMessages(messages);
        setThreadLimit(currentLimit);
        setHasMoreThread(messages.length >= currentLimit);
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          title: "Error",
          message: errorData.error || "Failed to load conversation",
        });
      }
    } catch (error) {
      console.error("Error loading thread:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to load conversation",
      });
    } finally {
      setLoadingThread(false);
      setLoadingMoreThread(false);
    }
  };

  const handleRefresh = () => {
    loadMessageHistory(true);
    loadSavedContacts();
    loadKnownContacts();
    loadHiddenConversations();
    if (selectedPhone) {
      shouldAutoScrollThreadRef.current = true;
      loadThreadMessages(selectedPhone, true);
    }
  };

  const handleHideConversation = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id || !selectedPhone) return;

    setHidingConversation(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/hidden-conversations`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            studio_id: studioUser.studio_id,
            phone_number: selectedPhone,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const hidden = data.hidden as SMSHiddenConversation;
        setHiddenConversations((prev) => {
          const filtered = prev.filter(
            (h) => !phonesMatch(h.phone_number, hidden.phone_number)
          );
          return [hidden, ...filtered];
        });
        setSelectedPhone(null);
        setShowHideConfirmModal(false);
        setNotification({
          show: true,
          title: "Conversation hidden",
          message:
            "This conversation is hidden from your list. Messages remain in Twilio.",
        });
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          title: "Error",
          message: errorData.error || "Failed to hide conversation",
        });
      }
    } catch (error) {
      console.error("Error hiding conversation:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to hide conversation",
      });
    } finally {
      setHidingConversation(false);
    }
  };

  const handleRestoreConversation = async (hidden: SMSHiddenConversation) => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/hidden-conversations/${hidden.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.ok) {
        setHiddenConversations((prev) =>
          prev.filter((h) => h.id !== hidden.id)
        );
        setNotification({
          show: true,
          title: "Conversation restored",
          message: "This conversation is visible in your list again.",
        });
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          title: "Error",
          message: errorData.error || "Failed to restore conversation",
        });
      }
    } catch (error) {
      console.error("Error restoring conversation:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to restore conversation",
      });
    }
  };

  const handleSaveName = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id || !selectedPhone) return;

    const trimmed = editNameValue.trim();
    if (!trimmed) return;

    setSavingName(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/contacts`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            studio_id: studioUser.studio_id,
            phone_number: selectedPhone,
            display_name: trimmed,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const contact = data.contact as SMSContact;
        setSavedContacts((prev) => {
          const filtered = prev.filter(
            (c) => !phonesMatch(c.phone_number, contact.phone_number)
          );
          return [...filtered, contact];
        });
        setShowEditModal(false);
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          title: "Error",
          message: errorData.error || "Failed to save contact name",
        });
      }
    } catch (error) {
      console.error("Error saving contact name:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to save contact name",
      });
    } finally {
      setSavingName(false);
    }
  };

  const openEditModal = () => {
    if (!selectedPhone) return;
    const saved = savedContacts.find((c) =>
      phonesMatch(c.phone_number, selectedPhone)
    );
    const known = knownContacts.get(phoneMatchKey(selectedPhone));
    setEditNameValue(saved?.display_name || known?.name || "");
    setShowEditModal(true);
  };

  const selectedSavedContact = selectedPhone
    ? savedContacts.find((c) => phonesMatch(c.phone_number, selectedPhone))
    : undefined;

  const handleDeleteName = async () => {
    const authToken = getAuthToken();
    if (!authToken || !selectedSavedContact) return;

    setDeletingName(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/contacts/${selectedSavedContact.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.ok) {
        setSavedContacts((prev) =>
          prev.filter((c) => c.id !== selectedSavedContact.id)
        );
        setShowEditModal(false);
        setShowDeleteNameConfirmModal(false);
        setNotification({
          show: true,
          title: "Custom name removed",
          message:
            "The saved name was removed. The contact will show as their student/lead name or phone number.",
        });
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          title: "Error",
          message: errorData.error || "Failed to remove contact name",
        });
      }
    } catch (error) {
      console.error("Error deleting contact name:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to remove contact name",
      });
    } finally {
      setDeletingName(false);
    }
  };

  const sortedThreadMessages = useMemo(
    () =>
      [...threadMessages].sort(
        (a, b) =>
          new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
      ),
    [threadMessages]
  );

  const scrollThreadToBottom = useCallback(() => {
    const container = threadContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  useEffect(() => {
    if (!selectedPhone || loadingThread || !shouldAutoScrollThreadRef.current) {
      return;
    }
    if (sortedThreadMessages.length === 0) return;

    scrollThreadToBottom();
    const frame = requestAnimationFrame(scrollThreadToBottom);
    const t1 = window.setTimeout(scrollThreadToBottom, 0);
    const t2 = window.setTimeout(scrollThreadToBottom, 100);
    shouldAutoScrollThreadRef.current = false;

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [selectedPhone, loadingThread, sortedThreadMessages, scrollThreadToBottom]);

  useEffect(() => {
    if (pendingScrollRestoreRef.current === null) return;
    const container = threadContainerRef.current;
    if (!container || loadingMoreThread) return;

    const saved = pendingScrollRestoreRef.current;
    requestAnimationFrame(() => {
      container.scrollTop =
        saved.scrollTop + (container.scrollHeight - saved.scrollHeight);
      pendingScrollRestoreRef.current = null;
    });
  }, [sortedThreadMessages, loadingMoreThread]);

  const selectedKnownType = selectedPhone
    ? knownContacts.get(phoneMatchKey(selectedPhone))?.type
    : undefined;

  const isInitialLoading =
    smsConfigLoading || loadingMessages || !hasAttemptedLoad;

  return (
    <PlanGatedPage
      capability="messagingSms"
      title="SMS History"
      subtitle="Conversations with your contacts"
    >
    <div className="relative flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 -mx-4 -my-8 xl:-mx-8 xl:-my-8 h-[calc(100vh-4rem)] min-h-[32rem]">
      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
      />

      <div className="flex-shrink-0 flex justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            SMS Message History
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Conversations with your contacts
          </p>
        </div>
        {smsConfig.isConfigured && (
          <button
            onClick={handleRefresh}
            disabled={loadingMessages}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <span className="pointer-events-none">
              {loadingMessages ? "Refreshing..." : "Refresh"}
            </span>
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col bg-white dark:bg-gray-800 overflow-hidden">
          {!smsConfigLoading && !smsConfig.isConfigured ? (
            <div className="text-center py-12 p-4">
              <PhoneIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                SMS is not configured for this studio
              </h3>
            </div>
          ) : isInitialLoading ? (
            <div className="flex flex-1 items-center justify-center min-h-0">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
            </div>
          ) : (
            <div className="flex flex-col md:flex-row flex-1 min-h-0 h-full">
              {/* Conversation list */}
              <div
                className={`md:w-80 lg:w-96 md:border-r border-gray-200 dark:border-gray-700 flex flex-col shrink-0 min-h-0 h-full ${
                  selectedPhone ? "hidden md:flex" : "flex"
                }`}
              >
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <input
                    type="text"
                    placeholder="Search by name, number, or message..."
                    value={conversationSearch}
                    onChange={(e) => setConversationSearch(e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                  {filteredConversations.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <PhoneIcon className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        No conversations found
                      </p>
                    </div>
                  ) : (
                    filteredConversations.map((conv) => {
                      const isSelected = selectedPhone
                        ? phonesMatch(conv.phone, selectedPhone)
                        : false;
                      const isInbound = !isMessageSent(conv.lastMessage);

                      return (
                        <button
                          key={conv.phone}
                          type="button"
                          onClick={() => {
                            shouldAutoScrollThreadRef.current = true;
                            setSelectedPhone(conv.phone);
                            setShowEditModal(false);
                          }}
                          className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                            isSelected
                              ? "bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-l-indigo-600"
                              : ""
                          }`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {getDisplayName(conv.phone)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {formatPhoneDisplay(conv.phone)}
                              </p>
                              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 truncate">
                                {isInbound ? "" : "You: "}
                                {conv.lastMessage.body}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                              {new Date(
                                conv.lastMessage.dateCreated
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {hasMoreMessages && visibleConversations.length > 0 && (
                  <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-center">
                    <button
                      onClick={() => loadMessageHistory(false)}
                      disabled={loadingMoreMessages}
                      className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-50"
                    >
                      {loadingMoreMessages
                        ? "Loading..."
                        : `Load more conversations (${messageHistory.length} messages loaded)`}
                    </button>
                  </div>
                )}

                {hiddenConversations.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setShowHiddenSection((v) => !v)}
                      className="w-full px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center justify-between"
                    >
                      <span className="flex items-center gap-2">
                        <EyeSlashIcon className="h-4 w-4" />
                        Hidden ({hiddenConversations.length})
                      </span>
                      <span className="text-xs text-gray-400">
                        {showHiddenSection ? "Hide" : "Show"}
                      </span>
                    </button>
                    {showHiddenSection && (
                      <div className="pb-2">
                        {hiddenConversations.map((hidden) => (
                          <div
                            key={hidden.id}
                            className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="min-w-0">
                              <p className="text-sm text-gray-900 dark:text-white truncate">
                                {getDisplayName(hidden.phone_number)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {formatPhoneDisplay(hidden.phone_number)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRestoreConversation(hidden)}
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex-shrink-0"
                              title="Restore conversation"
                            >
                              <ArrowPathIcon className="h-3.5 w-3.5" />
                              Restore
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Thread panel */}
              <div
                className={`flex-1 flex flex-col w-full min-w-0 min-h-0 h-full ${
                  selectedPhone ? "flex" : "hidden md:flex"
                }`}
              >
                {!selectedPhone ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center">
                      <PhoneIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                        Select a conversation to view message history
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setSelectedPhone(null)}
                        className="md:hidden text-sm text-indigo-600 dark:text-indigo-400 mb-2"
                      >
                        ← Back to conversations
                      </button>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate min-w-0">
                              {getDisplayName(selectedPhone)}
                            </h2>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={openEditModal}
                                className="inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors"
                                title="Edit contact name"
                              >
                                <PencilIcon className="h-4 w-4 pointer-events-none" />
                              </button>
                              {selectedSavedContact && (
                                <button
                                  type="button"
                                  onClick={() => setShowDeleteNameConfirmModal(true)}
                                  disabled={deletingName}
                                  className="inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:border-red-300 dark:hover:border-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Remove custom name"
                                >
                                  <TrashIcon className="h-4 w-4 pointer-events-none" />
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatPhoneDisplay(selectedPhone)}
                            {selectedKnownType && (
                              <span className="ml-2 capitalize">
                                · {selectedKnownType}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setShowHideConfirmModal(true)}
                            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                            title="Hide conversation"
                          >
                            <EyeSlashIcon className="h-4 w-4 mr-1.5 pointer-events-none" />
                            <span className="pointer-events-none">Hide</span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              navigate("/app/sms", {
                                state: {
                                  replyToPhone: selectedPhone,
                                  replyToDisplayName: getDisplayName(selectedPhone),
                                },
                              })
                            }
                            className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <ArrowUturnLeftIcon className="h-4 w-4 mr-1.5 pointer-events-none" />
                            <span className="pointer-events-none">Reply</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div
                      ref={threadContainerRef}
                      className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900/50 w-full"
                    >
                      {loadingThread ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                        </div>
                      ) : sortedThreadMessages.length === 0 ? (
                        <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-12">
                          No messages in this conversation
                        </p>
                      ) : (
                        <>
                          {hasMoreThread && (
                            <div className="text-center pb-1">
                              <button
                                type="button"
                                onClick={() =>
                                  loadThreadMessages(selectedPhone, false)
                                }
                                disabled={loadingMoreThread}
                                className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-50"
                              >
                                {loadingMoreThread
                                  ? "Loading..."
                                  : "Load older messages"}
                              </button>
                            </div>
                          )}
                          {sortedThreadMessages.map((msg) => {
                            const isSent = isMessageSent(msg);
                            const isFailed =
                              msg.status === "failed" ||
                              msg.status === "undelivered";

                            return (
                              <div
                                key={msg.sid}
                                className={`flex ${isSent ? "justify-end" : "justify-start"}`}
                              >
                                <div
                                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                    isSent
                                      ? "bg-indigo-600 text-white"
                                      : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600"
                                  }`}
                                >
                                  <p className="text-sm whitespace-pre-wrap break-words">
                                    {msg.body}
                                  </p>
                                  <div
                                    className={`mt-1 flex items-center gap-2 text-xs ${
                                      isSent
                                        ? "text-indigo-200"
                                        : "text-gray-500 dark:text-gray-400"
                                    }`}
                                  >
                                    <span>
                                      {new Date(msg.dateCreated).toLocaleString()}
                                    </span>
                                    {isSent && (
                                      <span
                                        className={
                                          isFailed ? "text-red-300" : ""
                                        }
                                      >
                                        · {msg.status}
                                      </span>
                                    )}
                                  </div>
                                  {msg.errorMessage && (
                                    <p className="mt-1 text-xs text-red-300">
                                      {msg.errorMessage}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
      </div>

        {showHideConfirmModal && selectedPhone && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="hide-conversation-title"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div
                className="fixed inset-0 bg-gray-500/75 dark:bg-gray-900/75"
                aria-hidden="true"
                onClick={() => !hidingConversation && setShowHideConfirmModal(false)}
              />
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
                <h3
                  id="hide-conversation-title"
                  className="text-lg font-medium text-gray-900 dark:text-white"
                >
                  Hide conversation?
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {getDisplayName(selectedPhone)}
                  </span>{" "}
                  ({formatPhoneDisplay(selectedPhone)}) will be removed from your
                  conversation list. Messages stay in Twilio and you can restore
                  this conversation later from Hidden.
                </p>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowHideConfirmModal(false)}
                    disabled={hidingConversation}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleHideConversation}
                    disabled={hidingConversation}
                    className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    {hidingConversation ? "Hiding..." : "Hide conversation"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteNameConfirmModal && selectedPhone && selectedSavedContact && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="delete-contact-name-title"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div
                className="fixed inset-0 bg-gray-500/75 dark:bg-gray-900/75"
                aria-hidden="true"
                onClick={() => !deletingName && setShowDeleteNameConfirmModal(false)}
              />
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
                <h3
                  id="delete-contact-name-title"
                  className="text-lg font-medium text-gray-900 dark:text-white"
                >
                  Remove saved name?
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Remove the custom name{" "}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedSavedContact.display_name}
                  </span>{" "}
                  for {formatPhoneDisplay(selectedPhone)}? This contact will
                  display as their student or lead name, or as the phone number
                  if no match is found. Messages are not deleted.
                </p>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteNameConfirmModal(false)}
                    disabled={deletingName}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteName}
                    disabled={deletingName}
                    className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    {deletingName ? "Removing..." : "Remove name"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEditModal && selectedPhone && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="edit-contact-title"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div
                className="fixed inset-0 bg-gray-500/75 dark:bg-gray-900/75"
                aria-hidden="true"
                onClick={() => !savingName && setShowEditModal(false)}
              />
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3
                    id="edit-contact-title"
                    className="text-lg font-medium text-gray-900 dark:text-white"
                  >
                    Edit contact name
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {formatPhoneDisplay(selectedPhone)}
                </p>
                <label
                  htmlFor="contact-display-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Display name
                </label>
                <input
                  id="contact-display-name"
                  type="text"
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  placeholder="Contact name"
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") setShowEditModal(false);
                  }}
                />
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    disabled={savingName}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveName}
                    disabled={savingName || !editNameValue.trim()}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {savingName ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
    </PlanGatedPage>
  );
}
